import { useRef, useState } from 'react';

import { Card } from 'primereact/card';
import { Messages } from 'primereact/messages';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import useEffectOnce from '../../hooks/useEffectOnce';
import LenderService from '../../services/lenderService';
import LenderModal from '../../components/admin/LenderModal';
import { Lender } from '../../models/lender';

const LendersPage = () => {
  const errors = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lenders, setLenders] = useState<Array<Lender>>([]);
  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });
  const [lender, setLender] = useState<Lender | null>(null);

  const lenderService = new LenderService();

  useEffectOnce(() => {
    loadLenders();
  });

  const loadLenders = () => {
    setLoading(true);
    lenderService
      .getLenders()
      .then((lenders) => {
        setLenders(lenders);
      })
      .catch((error) => {
        showError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const showError = (error: string) => {
    errors.current.show({ severity: 'error', summary: 'Error:', detail: error, life: 10000 });
  };

  const onGlobalFilterChange = (value: string) => {
    const _filters = { ...filters };
    _filters['global'].value = value;

    setFilters(_filters);
  };

  const onDelete = (lender: Lender) => {
    setLoading(true);
    lenderService
      .deleteLender(lender.lenderId!)
      .then(() => {
        loadLenders();
      })
      .catch((error) => {
        showError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const headerTemplate = () => {
    return (
      <div className="flex flex-row-reverse">
        <div>
          <span className="p-input-icon-left mr-2">
            <i className="pi pi-search" />
            <InputText
              type="search"
              value={filters['global'].value}
              onChange={(e) => onGlobalFilterChange(e.target.value)}
              placeholder="Search..."
            />
          </span>
          <Button label="New" icon="pi pi-plus" className="p-button-success" onClick={() => setLender(new Lender())} />
        </div>
      </div>
    );
  };

  const isActiveTemplate = (lender: Lender) => {
    const barStyle = {
      backgroundColor: lender.isActive ? 'green' : 'red',
      height: '1.5rem',
      width: '0.3rem',
      borderRadius: '10px',
    };
    return <div style={barStyle}></div>;
  };

  const regionsTemplate = (lender: Lender) => {
    return lender.regions.map((l) => l.regionId).join(', ');
  };

  const actionBodyTemplate = (lender: Lender) => {
    return (
      <div className="flex">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={() => setLender(lender)}
          tooltip="Edit Lender"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text"
          onClick={() =>
            confirmDialog({
              message: `Are you sure you want to delete this lender: ${lender.lenderName}?`,
              header: 'Confirmation',
              icon: 'pi pi-exclamation-triangle',
              accept: () => onDelete(lender),
            })
          }
          tooltip="Delete Lender"
        />
      </div>
    );
  };

  return (
    <Card title="Lenders">
      <Messages ref={errors} />
      <ConfirmDialog />
      <DataTable
        value={lenders}
        header={headerTemplate}
        responsiveLayout="scroll"
        loading={loading}
        filters={filters}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
        stripedRows
      >
        <Column field="isActive" body={isActiveTemplate} />
        <Column field="lenderId" header="Lender Id" />
        <Column field="lenderName" header="Name" />
        <Column field="lenderShortName" header="Short Name" />
        <Column field="regions" header="Regions" body={regionsTemplate} />
        <Column
          field="isClickToCallEnabled"
          header="Is Click To Call Enabled"
          body={(e) => (e.isClickToCallEnabled ? 'On' : 'Off')}
        />
        <Column
          field="isAccountExecutivesEnabled"
          header="Is Account Executives Enabled"
          body={(e) => (e.isAccountExecutivesEnabled ? 'On' : 'Off')}
        />
        <Column header="Actions" body={actionBodyTemplate} />
      </DataTable>

      {lender != null && <LenderModal lender={lender} onSave={loadLenders} onHide={() => setLender(null)} />}
    </Card>
  );
};

export default LendersPage;
